import { startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { groupBy, identity, Dictionary } from 'lodash';
import { ParticipantRole, ScheduledMeeting } from './types';

export const isHost = (meeting: ScheduledMeeting, userProfileId: number) =>
    meeting.participants.find(participant => participant.role === ParticipantRole.ATTENDEE_HOST)
        ?.userProfileId === userProfileId;

export const getAttendeeMeetingStatus = (meeting: ScheduledMeeting, userProfileId: number) =>
    meeting.participants.find(participant => participant.userProfileId === userProfileId)?.status;

export const getHostProfileId = (meeting: ScheduledMeeting) =>
    meeting.participants.find(user => user?.role === ParticipantRole.ATTENDEE_HOST)?.userProfileId;

export const getGuestProfileId = (meeting: ScheduledMeeting) =>
    meeting.participants.find(user => user?.role === ParticipantRole.ATTENDEE_GUEST)?.userProfileId;

export function groupByZonedDay<Date>(objects: Date[], timeZone: string): Dictionary<Date[]>;
export function groupByZonedDay<T>(
    objects: T[],
    timeZone: string,
    getDateFromObject: (t: T) => Date
): Dictionary<T[]>;
export function groupByZonedDay<T = Date>(
    objects: T[],
    timeZone: string,
    getDateFromObject: (t: T) => Date = identity
) {
    return groupBy(objects, obj => {
        const date = getDateFromObject(obj);
        const zonedTime = utcToZonedTime(date, timeZone);
        return startOfDay(zonedTime).getTime();
    });
}

export function groupByZonedDayToArray<Date>(
    objects: Date[],
    timeZone: string
): { day: number; values: Date[] }[];
export function groupByZonedDayToArray<T>(
    objects: T[],
    timeZone: string,
    getDateFromObject: (t: T) => Date
): { day: number; values: T[] }[];
export function groupByZonedDayToArray<T = Date>(
    objects: T[],
    timeZone: string,
    getDateFromObject: (t: T) => Date = identity
) {
    const grouped = groupByZonedDay(objects, timeZone, getDateFromObject);
    return Object.keys(grouped).reduce<{ day: number; values: T[] }[]>((acc, dayKeyString) => {
        const day = Number(dayKeyString); // lodash groupBy always outputs string keys :<
        return [...acc, { day, values: grouped[dayKeyString] }];
    }, []);
}
